import { SellerServicesRepository } from "../../domain/repositories/sellerServicesRepository"
import { Language } from "../../locale/interfaces/Translation"
import { SellerServicesDetailModel } from "../models/SellerServicesDetailModel"
import { SellerServicesService } from "../services/SellerServicesService"
import { SellerServiceDetail } from "../../domain/entities/SellerServiceDetail"
import { SellerService } from "../../domain/entities/SellerService"
import { SellerServiceCalendar } from "../../domain/entities/SellerServiceCalendar"
import { UploadMultipleSellerServicePictureResponse } from "../../domain/useCases/sellersServices/uploadMultipleSellerServicePicturesUseCase/response"
import { ServiceStatus } from "../../domain/enum/serviceStatus.enum"
import { TournamentMember } from "../../domain/entities/TournamentMember"

export class SellerServicesRepositoryImpl implements SellerServicesRepository {
	private _sellerServicesService
	constructor({ SellerServicesService }: { SellerServicesService: SellerServicesService }) {
		this._sellerServicesService = SellerServicesService
	}

	public async getSellerServices(
		page: number,
		limit: number,
		status?: string,
		isTournament?: boolean
	): Promise<SellerService[]> {
		const sellerServices = await this._sellerServicesService.getSellerServices(page, limit, status, isTournament)
		return sellerServices.map(sellerService => sellerService.toDomain())
	}

	// New API endpoint
	public async getServiceDetail(id: string): Promise<SellerServiceDetail> {
		const detail = await this._sellerServicesService.getServiceDetail(id)
		return detail.toDomain()
	}

	public async createSellerService(sellerService: SellerServiceDetail): Promise<SellerServiceDetail> {
		const detail = await this._sellerServicesService.createSellerService(
			SellerServicesDetailModel.fromDomain(sellerService)
		)
		return detail.toDomain()
	}

	public createServiceContent(sellerService: SellerServiceDetail): Promise<number> {
		return this._sellerServicesService.createServiceContent(SellerServicesDetailModel.fromDomain(sellerService))
	}

	public async updateSellerService(sellerService: SellerServiceDetail): Promise<Partial<SellerServiceDetail>> {
		const response = await this._sellerServicesService.updateSellerService(
			SellerServicesDetailModel.fromDomain(sellerService)
		)
		return response.toDomain()
	}

	public async updateSellerServiceStatus(id: number, status: ServiceStatus): Promise<void> {
		return this._sellerServicesService.deleteSellerService(id, status)
	}

	public async deleteServiceRollback(serviceId: string): Promise<{ success: boolean }> {
		return this._sellerServicesService.deleteServiceRollback(serviceId)
	}

	public async uploadServiceImages(
		serviceId: string,
		files: FormData
	): Promise<UploadMultipleSellerServicePictureResponse> {
		return await this._sellerServicesService.uploadServiceImages(serviceId, files)
	}

	public async searchSellerServices(
		expression: string,
		offset?: number,
		limit?: number,
		isTournament?: boolean
	): Promise<SellerService[]> {
		const response = await this._sellerServicesService.searchSellerServices(expression, offset, limit, isTournament)
		return response.map(sellerService => sellerService.toDomain())
	}

	public async getSellerServiceExcel(): Promise<Blob> {
		const response = await this._sellerServicesService.getSellerServiceExcel()
		return response
	}

	async getServicesBySellerId(sellerId: number): Promise<SellerService[]> {
		const response = await this._sellerServicesService.getServicesBySellerId(sellerId)
		return response.map(sellerService => sellerService.toDomain())
	}

	async getTournamentMembers(serviceId: number): Promise<TournamentMember[]> {
		const members = await this._sellerServicesService.getTournamentMembers(serviceId)
		return members.map(member => member.toDomain())
	}

	async downloadTournamentMembers(serviceId: number): Promise<Blob> {
		return await this._sellerServicesService.downloadTournamentMembers(serviceId)
	}

	async exportTournament(serviceId: number): Promise<Blob> {
		return await this._sellerServicesService.exportTournament(serviceId)
	}
}
